import React, { useEffect, useState, createContext, useRef } from "react";
import Peer from "simple-peer";
import usePermissions from "../../Hooks/hook-permissions";
import { getStream } from "../../../utils/globals/stream";
import { createBotAvatar } from "../../../utils/globals/avatarGenerator";
import { getUser as getAuthUser } from "../../../utils/globals/authUser";
import userStatus from "../../../config/defaults/userStatus";
import callTypes from "../../../config/defaults/callTypes";
import VideoChatChannel from "../../channels/VideoChat.channel";
import callStatus from "../../../config/defaults/callStatus";

const VideoChatContext = createContext();

let makeCall = () => {};
let acceptCall = () => {};
let cancelCall = () => {};
let completeCall = () => {};
let declineCall = () => {};
let ignoreCall = () => {};
let updateMeta = () => {};
let updateCallMediaTrack = () => {};

let peerConnection = null;

const ContextProvider = ({ children }) => {
  // state/ref
  const [myProfile, setMyProfile] = useState();
  const myProfileRef = useRef();
  myProfileRef.current = myProfile;

  const [mySocketId, setMySocketId] = useState();
  const mySocketIdRef = useRef();
  mySocketIdRef.current = mySocketId;

  const [myStream, setMyStream] = useState();
  const myStreamRef = useRef();
  myStreamRef.current = myStream;

  const [currentCall, setCurrentCall] = useState();
  const currentCallRef = useRef();
  currentCallRef.current = currentCall;

  const peerConnectionRef = useRef();

  // utilities
  const calculateCallDuration = (startedAt, completedAt) => {
    const seconds = Math.abs((completedAt - startedAt) / 1000);
    const time = new Date(seconds * 1000).toISOString().substr(14, 5);
    return time;
  };
  const updateCallProgress = (
    {
      from = null,
      attendee = null,
      type = null,
      signal = null,
      status = null,
      statusQue = null,
      startedAt = null,
      completedAt = null,
      duration = null,
    },
    unset = false
  ) => {
    if (unset) {
      setCurrentCall(Object.assign({}, {}));
      return;
    }

    const _oldStatus = {
      ...(currentCallRef.current ? currentCallRef.current : {}),
    };

    const _currentCall = {
      from: {
        ...(_oldStatus?.from ? _oldStatus.from : {}),
        ...(from ? from : {}),
      },
      attendee: {
        ...(_oldStatus?.attendee ? _oldStatus.attendee : {}),
        ...(attendee ? attendee : {}),
      },
      type: type ? type : _oldStatus?.type ? _oldStatus.type : null,
      signal: signal ? signal : _oldStatus?.signal ? _oldStatus.signal : null,
      status: status ? status : _oldStatus?.status ? _oldStatus.status : null,
      statusQue: [
        ...(_oldStatus?.statusQue ? _oldStatus.statusQue : []),
        ...(statusQue ? statusQue : []),
      ],
      startedAt: startedAt
        ? startedAt
        : _oldStatus?.startedAt
        ? _oldStatus.startedAt
        : null,
      completedAt: completedAt
        ? completedAt
        : _oldStatus?.completedAt
        ? _oldStatus.completedAt
        : null,
      duration: duration
        ? duration
        : _oldStatus?.duration
        ? _oldStatus.duration
        : null,
    };

    setCurrentCall(Object.assign({}, { ..._currentCall }));
  };

  // handlers
  const handleUserOnline = (socketId) => {
    setMySocketId(socketId);
  };
  const handleIncommingCallBackgroundIgnoreTask = () => {
    (async () => {
      const fromId = currentCallRef?.current?.from?.id;

      await new Promise((resolve) => setTimeout(resolve, 60000));
      if (
        currentCallRef?.current?.status === callStatus.RINGING &&
        currentCallRef?.current?.from?.id === fromId
      ) {
        ignoreCall(fromId);

        updateCallProgress({
          status: callStatus.NOT_ANSWERED,
          statusQue: [callStatus.NOT_ANSWERED],
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 30000));
      if (
        currentCallRef?.current?.status === callStatus.NOT_ANSWERED &&
        currentCallRef?.current?.from?.id === fromId
      ) {
        cancelCall(fromId);

        updateCallProgress({
          status: callStatus.CANCELED,
          statusQue: [callStatus.CANCELED],
        });
      }
    })();
  };
  const handleIncommingCall = (signal, fromId, fromName, fromAvatarFormula) => {
    updateCallProgress({
      from: {
        id: fromId,
        name: fromName,
        avatar: createBotAvatar(fromAvatarFormula),
        status: userStatus.ONLINE,
        mediaTrack: {
          audioEnabled: true,
          videoEnabled: true,
        },
      },
      attendee: {
        id: mySocketIdRef?.current,
        name: myProfileRef?.current?.name,
        avatar: createBotAvatar(myProfileRef?.current?.avatarFormula),
        status: userStatus.ONLINE,
        stream: myStreamRef.current,
        mediaTrack: {
          audioEnabled: true,
          videoEnabled: true,
        },
      },
      type: callTypes.INCOMMING,
      status: callStatus.RINGING,
      statusQue: [callStatus.RINGING],
      signal: signal,
    });

    handleIncommingCallBackgroundIgnoreTask();
  };
  const handleUserFound = (userId, userName, userAvatarFormula) => {
    updateCallProgress({
      attendee: {
        id: userId,
        name: userName,
        avatar: createBotAvatar(userAvatarFormula),
        status: userStatus.ONLINE,
      },
      status: callStatus.RINGING,
      statusQue: [callStatus.RINGING],
    });
  };
  const handleUserNotFound = (userId) => {
    updateCallProgress({
      attendee: {
        id: userId,
        status: userStatus.NOT_FOUND,
      },
      status: callStatus.FAILED,
      statusQue: [callStatus.FAILED],
    });
  };
  const handleCallAccepted = (signal) => {
    updateCallProgress({
      status: callStatus.ACCEPTED,
      statusQue: [callStatus.ACCEPTED],
      signal: signal,
      startedAt: new Date(),
    });

    peerConnection.signal(signal);
  };
  const handleCallCanceled = (fromId) => {
    updateCallProgress({
      status: callStatus.CANCELED,
      statusQue: [callStatus.CANCELED],
    });

    peerConnection = null;
    peerConnectionRef.current = null;
  };
  const handleUserOffline = (id) => {
    if (currentCallRef?.current?.status === callStatus.COMPLETED) return;

    if (id === currentCallRef?.current?.attendee?.id) {
      updateCallProgress({
        attendee: {
          status: userStatus.OFFLINE,
        },
        status: callStatus.COMPLETED,
        statusQue: [callStatus.COMPLETED],
      });
    }

    if (id === currentCallRef?.current?.from?.id) {
      updateCallProgress({
        from: {
          status: userStatus.OFFLINE,
        },
        status: callStatus.COMPLETED,
        statusQue: [callStatus.COMPLETED],
      });
    }
  };
  const handleCallDeclined = (fromId) => {
    updateCallProgress({
      status: callStatus.DECLINED,
      statusQue: [callStatus.DECLINED],
    });
  };
  const handleCallCompleted = () => {
    updateCallProgress({
      status: callStatus.COMPLETED,
      statusQue: [callStatus.COMPLETED],
      completedAt: new Date(),
      duration: calculateCallDuration(
        currentCallRef?.current?.startedAt,
        new Date()
      ),
    });

    peerConnectionRef.current = null;
    currentCallRef.current = null;
  };
  const handleCallIgnored = (fromId) => {
    updateCallProgress({
      status: callStatus.NOT_ANSWERED,
      statusQue: [callStatus.NOT_ANSWERED],
    });
  };
  const handleCallMediaTrackUpdated = (fromId, mediaState) => {
    const iAmAttendee = currentCallRef?.current?.from?.id === fromId;

    if (iAmAttendee) {
      updateCallProgress({
        from: {
          mediaTrack: mediaState,
        },
      });
    } else {
      updateCallProgress({
        attendee: {
          mediaTrack: mediaState,
        },
      });
    }
  };

  // triggers
  const triggerVideoChatChannel = () => {
    const {
      makeCall: _makeCall,
      acceptCall: _acceptCall,
      cancelCall: _cancelCall,
      completeCall: _completeCall,
      declineCall: _declineCall,
      ignoreCall: _ignoreCall,
      updateMeta: _updateMeta,
      updateCallMediaTrack: _updateCallMediaTrack,
    } = VideoChatChannel({
      onUserOnline: handleUserOnline,
      onIncommingCall: handleIncommingCall,
      onUserOffline: handleUserOffline,
      onCallCanceled: handleCallCanceled,
      onCallCompleted: handleCallCompleted,
      onUserFound: handleUserFound,
      onUserNotFound: handleUserNotFound,
      onCallAccepted: handleCallAccepted,
      onCallDeclined: handleCallDeclined,
      onCallIgnored: handleCallIgnored,
      onCallMediaTrackUpdated: handleCallMediaTrackUpdated,
    });

    makeCall = _makeCall;
    acceptCall = _acceptCall;
    cancelCall = _cancelCall;
    completeCall = _completeCall;
    declineCall = _declineCall;
    ignoreCall = _ignoreCall;
    updateMeta = _updateMeta;
    updateCallMediaTrack = _updateCallMediaTrack;
  };
  const triggerGetUser = () => {
    const _user = getAuthUser() || {};
    setMyProfile({ ..._user });

    updateMeta(_user?.name, _user?.avatarFormula);
  };
  const triggerGetStream = async () => {
    const stream = await getStream();
    setMyStream(stream);
  };
  const triggerStopStreamTracks = async () => {
    myStream?.getTracks()?.forEach(track => track?.stop());
  };
  const triggerUpdateMediaStream = (stream) => {
    setMyStream(stream)
  }
  const triggerOutgoingCall = (toId) => {
    updateCallProgress({
      from: {
        id: mySocketIdRef?.current,
        name: myProfileRef?.current?.name,
        avatar: createBotAvatar(myProfileRef?.current?.avatarFormula),
        status: userStatus?.ONLINE,
        stream: myStreamRef.current,
        mediaTrack: {
          audioEnabled: true,
          videoEnabled: true,
        },
      },
      attendee: {
        id: toId,
        mediaTrack: {
          audioEnabled: true,
          videoEnabled: true,
        },
      },
      type: callTypes.OUTGOING,
      status: callStatus.CALLING,
      statusQue: [callStatus.CALLING],
    });

    // peer instance
    peerConnection = new Peer({
      initiator: true,
      trickle: false,
      stream: myStream,
    });

    /**
     * When the peer connection is set then make the call(outgoing)
     */
    peerConnection.on("signal", (signal) => {
      updateCallProgress({ signal: signal });

      makeCall(
        signal,
        mySocketId,
        myProfile?.name || mySocketId,
        myProfile?.avatarFormula,
        toId
      );
    });

    /**
     * When the call is accepted and attendee stream from server is received
     */
    peerConnection.on("stream", (stream) => {
      updateCallProgress({
        attendee: {
          stream: stream,
        },
      });
    });
  };
  const triggerCancelOutgoingCall = () => {
    cancelCall(currentCallRef?.current?.attendee?.id);

    updateCallProgress({}, true);

    peerConnection = null;
    peerConnectionRef.current = null;
  };
  const triggerAcceptIncommingCall = () => {
    updateCallProgress({
      status: callStatus.ACCEPTED,
      statusQue: [callStatus.ACCEPTED],
    });

    // peer instance
    const peerConnection = new Peer({
      initiator: false,
      trickle: false,
      stream: myStreamRef?.current,
    });

    /**
     * When the peer connection is set then accept the call(incomming)
     */
    peerConnection.on("signal", (signal) => {
      updateCallProgress({
        startedAt: new Date(),
        signal: signal,
      });

      acceptCall(signal, currentCallRef?.current?.from?.id);
    });

    /**
     * When the call is accepted and attendee stream from server is received
     */
    peerConnection.on("stream", (stream) => {
      updateCallProgress({
        from: {
          stream: stream,
        },
      });
    });

    peerConnection.signal(currentCallRef?.current?.signal);
    peerConnectionRef.current = peerConnection;
  };
  const triggerDeclineIncommingCall = () => {
    declineCall(currentCallRef?.current?.from?.id);

    updateCallProgress({}, true);

    peerConnectionRef.current = null;
    currentCallRef.current = null;
  };
  const triggerUnsetCurrentCall = () => {
    updateCallProgress({}, true);

    peerConnectionRef.current = null;
    currentCallRef.current = null;
  };
  const triggerCompleteCall = () => {
    completeCall(
      currentCallRef?.current?.type === callTypes.INCOMMING
        ? currentCallRef?.current?.from?.id
        : currentCallRef?.current?.attendee?.id
    );

    updateCallProgress({
      status: callStatus.COMPLETED,
      statusQue: [callStatus.COMPLETED],
      completedAt: new Date(),
      duration: calculateCallDuration(
        currentCallRef?.current?.startedAt,
        new Date()
      ),
    });

    // remove peer instance
    peerConnectionRef.current = null;
    currentCallRef.current = null;
  };
  const triggerUpdateCallMediaTrack = ({ audioEnabled, videoEnabled }) => {
    const mediaState = { audioEnabled, videoEnabled };
    const iAmAttendee = currentCallRef?.current?.from?.id !== mySocketId;
    const toId = iAmAttendee
      ? currentCallRef?.current?.from?.id
      : currentCallRef?.current?.attendee?.id;

    updateCallMediaTrack(toId, mediaState);

    if (iAmAttendee) {
      updateCallProgress({
        attendee: {
          mediaTrack: mediaState,
        },
      });
    } else {
      updateCallProgress({
        from: {
          mediaTrack: mediaState,
        },
      });
    }
  };

  // custom hooks
  const {
    hasCameraPermission,
    hasAudioPermission,
    isCameraPermissionBlocked,
    isAudioPermissionBlocked,
  } = usePermissions();

  // effects
  useEffect(() => {
    if (
      hasCameraPermission &&
      hasAudioPermission &&
      !isCameraPermissionBlocked &&
      !isAudioPermissionBlocked
    ) {
      triggerVideoChatChannel();
      triggerGetUser();
      triggerGetStream();
    }
  }, [
    hasCameraPermission,
    hasAudioPermission,
    isCameraPermissionBlocked,
    isAudioPermissionBlocked,
  ]);

  return (
    <VideoChatContext.Provider
      value={{
        myProfile,
        mySocketId,
        myStream,
        currentCall,
        triggerGetUser,
        triggerStopStreamTracks,
        triggerUpdateMediaStream,
        triggerOutgoingCall,
        triggerCancelOutgoingCall,
        triggerAcceptIncommingCall,
        triggerDeclineIncommingCall,
        triggerUnsetCurrentCall,
        triggerCompleteCall,
        triggerUpdateCallMediaTrack,
      }}
    >
      {children}
    </VideoChatContext.Provider>
  );
};

export { ContextProvider, VideoChatContext };
